import React, { useEffect, useState } from "react";
import { UserPlus, Edit2, X, Save } from "lucide-react";
import { AdminUser, AuditLogEntry, SystemSettings, AdminRole } from "../types";
import { Badge, Button, Input, Search, Tooltip } from "../../components/core";

type SystemAdminView = "users" | "audit" | "settings";

interface SystemAdminProps {
  adminUsers: AdminUser[];
  auditLogs: AuditLogEntry[];
  settings: SystemSettings;
  currentRole: AdminRole;
  /** Controlled by AdminPortal top tabs — hides duplicate inner tabs */
  activeView?: SystemAdminView;
  onAddAdminUser: (u: AdminUser) => void;
  onUpdateAdminUser: (u: AdminUser) => void;
  onUpdateSettings: (s: SystemSettings) => void;
  addToast: (
    type: "success" | "error" | "info" | "warning",
    title: string,
    message: string
  ) => void;
}

const VIEW_TITLES: Record<SystemAdminView, string> = {
  users: "Portal Admin Users & Role Permissions",
  audit: "Security Audit Logs",
  settings: "Global Portal Configurations",
};

export const SystemAdmin: React.FC<SystemAdminProps> = ({
  adminUsers,
  auditLogs,
  settings,
  currentRole,
  activeView = "users",
  onAddAdminUser,
  onUpdateAdminUser,
  onUpdateSettings,
  addToast,
}) => {
  const [subTab, setSubTab] = useState<SystemAdminView>(activeView);
  const [auditSearch, setAuditSearch] = useState("");

  useEffect(() => {
    setSubTab(activeView);
  }, [activeView]);

  const isSuperAdmin = currentRole === "SuperAdmin";

  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null);
  const [userForm, setUserForm] = useState({
    username: "",
    name: "",
    email: "",
    role: "Admin" as AdminRole,
    status: "Active" as "Active" | "Inactive",
  });

  const [settingsForm, setSettingsForm] = useState({ ...settings });

  const filteredLogs = auditLogs.filter(
    l =>
      l.actorName.toLowerCase().includes(auditSearch.toLowerCase()) ||
      l.details.toLowerCase().includes(auditSearch.toLowerCase()) ||
      l.module.toLowerCase().includes(auditSearch.toLowerCase())
  );

  const openAddUser = () => {
    if (!isSuperAdmin) {
      addToast(
        "error",
        "Permission Denied",
        "Only SuperAdmins / Trustees can manage user accounts."
      );
      return;
    }
    setEditingUser(null);
    setUserForm({
      username: "",
      name: "",
      email: "",
      role: "Admin",
      status: "Active",
    });
    setIsUserModalOpen(true);
  };

  const handleUserSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isSuperAdmin) {
      return;
    }

    if (editingUser) {
      onUpdateAdminUser({
        ...editingUser,
        ...userForm,
      });
      addToast("success", "User Updated", `Updated credentials for ${userForm.name}`);
    } else {
      const newUser: AdminUser = {
        id: `USR-${Math.floor(100 + Math.random() * 900)}`,
        ...userForm,
        avatarUrl:
          "https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&q=80&w=200",
        lastLogin: "Never",
      };
      onAddAdminUser(newUser);
      addToast(
        "success",
        "User Created",
        `Created ${newUser.role} user ${newUser.username}`
      );
    }
    setIsUserModalOpen(false);
  };

  const handleSaveSettings = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isSuperAdmin) {
      addToast(
        "error",
        "Permission Denied",
        "Only SuperAdmins can update global system settings."
      );
      return;
    }
    onUpdateSettings(settingsForm);
    addToast(
      "success",
      "System Settings Saved",
      "Global Pathshala configurations updated successfully."
    );
  };

  return (
    <div className="w-full space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-bold tracking-tight text-[var(--gv-ink)]">
            {VIEW_TITLES[subTab]}
          </h1>
          <p className="mt-0.5 text-xs text-[var(--gv-ink-muted)]">
            System Admin · Security & Audit
          </p>
        </div>

        {subTab === "users" && isSuperAdmin && (
          <Button leftIcon={UserPlus} onClick={openAddUser}>
            Create Admin User
          </Button>
        )}
      </div>

      {subTab === "users" && (
        <div className="admin-card w-full overflow-hidden">
          <table className="w-full text-left text-xs text-[var(--gv-ink-muted)]">
            <thead className="border-b border-[var(--gv-border)] bg-stone-50 font-mono text-[10px] uppercase tracking-wider text-[var(--gv-ink-muted)]">
              <tr>
                <th className="px-4 py-2.5 font-bold">User</th>
                <th className="px-4 py-2.5 font-bold">Username & Email</th>
                <th className="px-4 py-2.5 font-bold">Assigned Role</th>
                <th className="px-4 py-2.5 font-mono font-bold">Last Login</th>
                <th className="px-4 py-2.5 font-bold">Status</th>
                <th className="px-4 py-2.5 text-right font-bold">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100 bg-white font-medium">
              {adminUsers.map(u => (
                <tr key={u.id} className="transition-colors hover:bg-stone-50/80">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <img
                        src={u.avatarUrl}
                        alt={u.name}
                        className="h-8 w-8 rounded-full border border-[var(--gv-border)] object-cover"
                      />
                      <div>
                        <div className="font-bold text-[var(--gv-ink)]">{u.name}</div>
                        <div className="font-mono text-[10px] text-[var(--gv-brand)]">
                          {u.id}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-mono font-semibold text-[var(--gv-ink)]">
                      {u.username}
                    </div>
                    <div className="text-[10px] text-[var(--gv-ink-muted)]">{u.email}</div>
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant="accent">{u.role}</Badge>
                  </td>
                  <td className="px-4 py-3 font-mono text-[var(--gv-ink-muted)]">
                    {u.lastLogin}
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant={u.status === "Active" ? "success" : "outline"}>
                      {u.status}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {isSuperAdmin && (
                      <Tooltip content="Edit user">
                        <Button
                          variant="ghost"
                          size="icon"
                          leftIcon={Edit2}
                          aria-label={`Edit ${u.name}`}
                          onClick={() => {
                            setEditingUser(u);
                            setUserForm({
                              username: u.username,
                              name: u.name,
                              email: u.email,
                              role: u.role,
                              status: u.status,
                            });
                            setIsUserModalOpen(true);
                          }}
                        />
                      </Tooltip>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {subTab === "audit" && (
        <div className="w-full space-y-4">
          <div className="admin-card flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
            <Search
              value={auditSearch}
              onChange={setAuditSearch}
              placeholder="Filter audit logs by actor or action..."
              containerClassName="w-full max-w-md"
            />
            <span className="font-mono text-xs text-[var(--gv-ink-muted)]">
              Showing {filteredLogs.length} audit events
            </span>
          </div>

          <div className="admin-card w-full overflow-hidden">
            <table className="w-full text-left text-xs text-[var(--gv-ink-muted)]">
              <thead className="border-b border-[var(--gv-border)] bg-stone-50 font-mono text-[10px] uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-2.5 font-bold">Log ID & Time</th>
                  <th className="px-4 py-2.5 font-bold">Actor</th>
                  <th className="px-4 py-2.5 font-bold">Action & Module</th>
                  <th className="px-4 py-2.5 font-bold">Event Details</th>
                  <th className="px-4 py-2.5 font-bold">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100 bg-white font-medium">
                {filteredLogs.map(log => (
                  <tr key={log.id} className="transition-colors hover:bg-stone-50/80">
                    <td className="px-4 py-3 font-mono">
                      <div className="font-bold text-[var(--gv-brand)]">{log.id}</div>
                      <div className="text-[10px] text-[var(--gv-ink-muted)]">
                        {log.timestamp}
                      </div>
                    </td>
                    <td className="px-4 py-3 font-bold text-[var(--gv-ink)]">
                      {log.actorName}{" "}
                      <span className="font-normal text-[var(--gv-ink-muted)]">
                        ({log.actorRole})
                      </span>
                    </td>
                    <td className="px-4 py-3 font-mono text-[var(--gv-ink)]">
                      <div>{log.action}</div>
                      <div className="text-[10px] text-[var(--gv-ink-muted)]">{log.module}</div>
                    </td>
                    <td className="max-w-md px-4 py-3 text-[11px] leading-relaxed text-[var(--gv-ink-muted)]">
                      {log.details}
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant="success">{log.status}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {subTab === "settings" && (
        <div className="admin-card w-full space-y-5 p-6">
          <form onSubmit={handleSaveSettings} className="space-y-5 text-xs">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
              <Input
                label="Pathshala Name"
                required
                value={settingsForm.pathshalaName}
                onChange={e =>
                  setSettingsForm({ ...settingsForm, pathshalaName: e.target.value })
                }
              />
              <Input
                label="Academic Year"
                required
                className="font-mono"
                value={settingsForm.academicYear}
                onChange={e =>
                  setSettingsForm({ ...settingsForm, academicYear: e.target.value })
                }
              />
              <Input
                label="Daily Niyam Cutoff"
                type="time"
                required
                className="font-mono"
                value={settingsForm.dailyNiyamCutoffTime}
                onChange={e =>
                  setSettingsForm({
                    ...settingsForm,
                    dailyNiyamCutoffTime: e.target.value,
                  })
                }
              />
              <Input
                label="Contact Phone"
                required
                className="font-mono"
                value={settingsForm.contactPhone}
                onChange={e =>
                  setSettingsForm({ ...settingsForm, contactPhone: e.target.value })
                }
              />
              <Input
                label="Contact Email"
                type="email"
                required
                value={settingsForm.contactEmail}
                onChange={e =>
                  setSettingsForm({ ...settingsForm, contactEmail: e.target.value })
                }
              />
            </div>

            {isSuperAdmin && (
              <div className="flex justify-end border-t border-[var(--gv-border)] pt-4">
                <Button type="submit" leftIcon={Save} size="lg">
                  Save Global Settings
                </Button>
              </div>
            )}
          </form>
        </div>
      )}

      {isUserModalOpen && (
        <div className="fixed inset-0 z-[9990] flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-xs">
          <div className="admin-card w-full max-w-md space-y-4 p-6 text-[var(--gv-ink)] shadow-xl">
            <div className="flex items-center justify-between border-b border-[var(--gv-border)] pb-3">
              <h3 className="text-base font-bold">
                {editingUser
                  ? `Edit User: ${editingUser.username}`
                  : "Create Portal Admin User"}
              </h3>
              <Button
                variant="ghost"
                size="icon"
                leftIcon={X}
                aria-label="Close"
                onClick={() => setIsUserModalOpen(false)}
              />
            </div>

            <form onSubmit={handleUserSubmit} className="space-y-3 text-xs">
              <Input
                label="Full Name"
                required
                placeholder="Ketanbhai Mehta"
                value={userForm.name}
                onChange={e => setUserForm({ ...userForm, name: e.target.value })}
              />
              <Input
                label="Username"
                required
                className="font-mono"
                placeholder="ketan.mehta"
                value={userForm.username}
                onChange={e => setUserForm({ ...userForm, username: e.target.value })}
              />
              <Input
                label="Email"
                type="email"
                required
                placeholder="ketan@jainpathshala.org"
                value={userForm.email}
                onChange={e => setUserForm({ ...userForm, email: e.target.value })}
              />

              <div className="w-full space-y-1.5">
                <label className="block text-xs font-bold text-[var(--gv-ink)]">
                  Assigned Role
                </label>
                <select
                  value={userForm.role}
                  onChange={e =>
                    setUserForm({ ...userForm, role: e.target.value as AdminRole })
                  }
                  className="admin-input h-9 w-full rounded-xl border border-[var(--gv-border)] bg-white px-3.5 py-2 text-xs text-[var(--gv-ink)] focus-visible:border-[var(--gv-brand)] focus-visible:outline-none"
                >
                  <option value="SuperAdmin">SuperAdmin / Trustee</option>
                  <option value="Admin">Admin / Office Staff</option>
                  <option value="Guruji">Guruji / Teacher</option>
                  <option value="Auditor">View-Only Auditor</option>
                </select>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setIsUserModalOpen(false)}
                >
                  Cancel
                </Button>
                <Button type="submit">Save User Account</Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
